import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { TEMPLATE_CATEGORIES } from '@/modules/templates/constants/template-categories';
import { getTemplateIcon } from '@/modules/templates/constants/template-icon-map';
import type { Template } from '@/modules/templates/types/templates.types';
import { getCreateHref } from '@/modules/templates/utils/template-href';
import { ArrowUpRight, Clock, Sparkles } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ContractsTable, {
  type ContractsTableColumn,
} from '../../contracts/components/contracts-table';
import { PUBLIC_TEMPLATES_PAGE_SIZE, type PublicTemplatesController } from './use-public-templates';

interface PublicTemplatesListProps {
  controller: PublicTemplatesController;
}

/** Public Templates tab body: a paginated data table of pure public templates. */
export function PublicTemplatesList({ controller }: PublicTemplatesListProps) {
  const { t } = useTranslation();
  const { templates, totalCount, totalPages, page, setPage, isLoading } = controller;

  const columns = useMemo<ContractsTableColumn<Template>[]>(
    () => [
      {
        id: 'title',
        header: t('ORGCONTRACTS_COLUMNS_TITLE'),
        cell: (template) => {
          const Icon = getTemplateIcon(template.icon);
          return (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 shrink-0 rounded-lg bg-selise-blue/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-selise-blue" />
              </div>
              <div className="min-w-0 space-y-1">
                <p className="max-w-90 truncate text-[15px] font-semibold leading-5 text-[hsl(var(--fg))]">
                  {template.title}
                </p>
                {template.description && (
                  <p className="max-w-90 truncate text-sm text-globe-grey">
                    {template.description}
                  </p>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'category',
        header: t('ORGTEMPLATES_COLUMNS_CATEGORY'),
        cell: (template) => {
          const categoryInfo = TEMPLATE_CATEGORIES.find((c) => c.slug === template.category);
          if (!categoryInfo) return <span className="text-sm text-globe-grey">-</span>;
          const CategoryIcon = categoryInfo.icon;
          return (
            <Badge className="w-fit gap-1.5 bg-selise-blue/12 text-selise-blue">
              <CategoryIcon className="h-3 w-3" />
              {categoryInfo.label}
            </Badge>
          );
        },
      },
      {
        id: 'popular',
        header: t('ORGTEMPLATES_COLUMNS_POPULAR'),
        cell: (template) =>
          template.popular ? (
            <Badge className="w-fit gap-1.5 bg-amber-500/12 text-amber-600">
              <Sparkles className="h-3 w-3" />
              {t('TEMPLATES_MOSTPOPULAR')}
            </Badge>
          ) : (
            <span className="text-sm text-globe-grey">-</span>
          ),
      },
      {
        id: 'estimatedMinutes',
        header: t('ORGTEMPLATES_COLUMNS_DURATION'),
        cell: (template) =>
          template.estimatedMinutes ? (
            <span className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[hsl(var(--fg))]">
              <Clock className="h-4 w-4 text-globe-grey" />
              {t('ORGTEMPLATES_MINUTES', { minutes: template.estimatedMinutes })}
            </span>
          ) : (
            <span className="text-sm text-globe-grey">-</span>
          ),
      },
      {
        id: 'action',
        header: t('ORGCONTRACTS_COLUMNS_ACTION'),
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (template) => (
          <Button size="sm" asChild variant="primary">
            <Link to={getCreateHref(template)}>
              {t('ORGTEMPLATES_PUBLIC_CREATE')}
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        ),
      },
    ],
    [t]
  );

  return (
    <ContractsTable
      columns={columns}
      data={templates}
      rowKey={(template) => template.id}
      isLoading={isLoading}
      emptyMessage={t('ORGTEMPLATES_PUBLIC_EMPTY')}
      pagination={{
        page,
        limit: PUBLIC_TEMPLATES_PAGE_SIZE,
        total: totalCount,
        totalPages,
      }}
      onPageChange={setPage}
    />
  );
}
